import React, { useState, useEffect } from 'react';
import type { View } from '../../types';
import type { PublicationCategory } from '../../lib/api/types';
import { ChevronLeftIcon, ChevronDownIcon, UploadIcon, BinIcon } from '../icons';
import { usePublication, useCreatePublication, useUpdatePublication, usePublicationCategories } from '../../lib/api/hooks/publication';
import { useFileUpload, useFileDelete } from '../../lib/api/hooks/file-upload';
import { showToast } from '../../lib/toast';

interface PublicationEditorProps {
  publicationId?: string;
  setView: (view: View) => void;
}

interface UploadedFile {
  name: string;
  size: number;
  url: string;
}

const PublicationEditor: React.FC<PublicationEditorProps> = ({ publicationId, setView }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isHighlighted, setHighlighted] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);


  const { data: publicationData, isLoading } = usePublication(publicationId || '');
  const publication = publicationData?.data;

  const { data: categoriesData, isLoading: isCategoriesLoading } = usePublicationCategories();
  const categories = categoriesData?.data || [];

  const createMutation = useCreatePublication();
  const updateMutation = useUpdatePublication();
  const uploadMutation = useFileUpload();
  const deleteMutation = useFileDelete();

  useEffect(() => {
    if (publication) {
      setTitle(publication.title);
      setDescription(publication.description);
      setCategory(publication.publicationCategory);
      setHighlighted(publication.legalFoundation);

   
      if (publication.publicationUrl) {
        setUploadedFile({
          name: publication.title + '.pdf',
          size: 0,
          url: publication.publicationUrl
        });
      }
    }
  }, [publication]);

  const uploadFileToServer = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadResult = await uploadMutation.mutateAsync({
        file,
        category: 'DOCUMENT'
      });

      if (!uploadResult.success || !uploadResult.data) {
        throw new Error('File upload failed');
      }

    
      const fileUrl = uploadResult.data.fileUrl;

      if (!fileUrl) {
        throw new Error('File URL not found in response');
      }

      setUploadedFile({
        name: file.name,
        size: file.size,
        url: fileUrl
      });

      showToast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload failed:', error);
      showToast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      uploadFileToServer(file);
    } else if (file && file.type !== 'application/pdf') {
      showToast.error('Please upload a PDF file');
    } else if (file && file.size > 10 * 1024 * 1024) {
      showToast.error('File size must be less than 10MB');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      uploadFileToServer(file);
    } else if (file && file.type !== 'application/pdf') {
      showToast.error('Please upload a PDF file');
    } else if (file && file.size > 10 * 1024 * 1024) {
      showToast.error('File size must be less than 10MB');
    }
  };

  const handleDeleteFile = async () => {
    if (uploadedFile?.url && publicationId) {
      try {
        await deleteMutation.mutateAsync(uploadedFile.url);
        showToast.success('File deleted successfully');
      } catch (error) {
        console.error('Failed to delete file:', error);
        showToast.error('Failed to delete file');
      }
    }
    setUploadedFile(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)}kb`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(0)}mb`;
  };

  
  const isFormValid = () => {
    return (
      title.trim() !== '' &&
      description.trim() !== '' &&
      category !== '' &&
      uploadedFile !== null
    );
  };

  const handleSave = async (status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      // Validate form
      if (!title.trim()) {
        showToast.error('Please enter a title');
        setLoading(false);
        return;
      }

      if (!description.trim()) {
        showToast.error('Please enter a description');
        setLoading(false);
        return;
      }

      if (!category) {
        showToast.error('Please select a category');
        setLoading(false);
        return;
      }

      if (!uploadedFile || !uploadedFile.url) {
        showToast.error('Please upload a PDF file');
        setLoading(false);
        return;
      }

      const payload = {
        fileUrl: uploadedFile.url,
        title: title.trim(),
        description: description.trim(),
        publicationCategory: category,
        legalFoundation: isHighlighted,
        status
      };

      if (publicationId) {
       
        await updateMutation.mutateAsync({
          publicationId,
          data: payload
        });
        showToast.success(`Publication ${status === 'PUBLISH' ? 'published' : 'saved as draft'} successfully`);
      } else {
      
        await createMutation.mutateAsync(payload);
        showToast.success(`Publication ${status === 'PUBLISH' ? 'published' : 'created as draft'} successfully`);
      }

     
      setView({ page: 'Publications' });
    } catch (error) {
      console.error('Failed to save publication:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to save publication');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading && publicationId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading publication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl  ">
      <header className="p-4 flex justify-between items-center border-b border-gray-200">
        <button
          onClick={() => setView({ page: 'Publications' })}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          disabled={isDraftSaving || isPublishing || isUploading}
        >
          <ChevronLeftIcon className="w-5 h-5" />
          Go back
        </button>
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleSave('DRAFT')}
            disabled={isDraftSaving || isPublishing || isUploading}
            className="text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg px-6 py-2 hover:bg-gray-200 disabled:opacity-50"
          >
            {isDraftSaving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={() => handleSave('PUBLISH')}
            disabled={!isFormValid() || isDraftSaving || isPublishing || isUploading}
            className="text-sm font-semibold text-white bg-forestGreen-100 rounded-lg px-6 py-2 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </header>

      <div className="p-6 md:p-10 max-w-2xl mx-auto space-y-6">
        {isUploading ? (
          <div className="text-center border-2 border-dashed border-gray-300 rounded-lg p-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00764B]"></div>
            <p className="mt-4 font-semibold text-gray-700">Uploading file...</p>
            <p className="text-xs text-gray-500 mt-1">Please wait while we upload your file</p>
          </div>
        ) : !uploadedFile ? (
          <div
            className="text-center border-2 border-dashed border-gray-300 rounded-lg p-10 cursor-pointer"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <UploadIcon className="w-12 h-12 mx-auto text-[#00764B] pointer-events-none" />
            <p className="mt-2 font-semibold text-gray-700 pointer-events-none">Drag file to upload</p>
            <p className="text-xs text-gray-500 mt-1 pointer-events-none">Supported document file is PDF with max size of 10mb</p>
            <span className="mt-4 inline-block text-sm font-semibold text-[#00764B] hover:underline bg-[#ECFEF8] p-3 pointer-events-none">
              Browse File
            </span>
            <input
              id="file-upload"
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 border border-gray-300 rounded-lg">
            <div className="flex items-center gap-3">
              <img
                src="/pdfIcon.png"
                alt="PDF"
                className="w-12 h-12"
              />
              <div>
                <p className="font-semibold text-gray-900">{uploadedFile.name}</p>
                <p className="text-sm text-gray-500">{formatFileSize(uploadedFile.size)}</p>
              </div>
            </div>
            <button
              onClick={handleDeleteFile}
              className="p-2 bg-red-50 rounded-lg transition-colors"
            >
              <BinIcon className="w-5 h-5 text-red-500 " />
            </button>
          </div>
        )}

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input type="text" id="title" placeholder="Enter" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300" />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea id="description" placeholder="Enter" value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300"></textarea>
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <div className="relative">
            <select
              id="category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full appearance-none bg-white px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
              disabled={isCategoriesLoading}
            >
              <option value="" disabled>
                {isCategoriesLoading ? 'Loading categories...' : 'Select Category'}
              </option>
              {categories.map((cat: PublicationCategory) => (
                <option key={cat.name} value={cat.name}>
                  {cat.value}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
            <label className="flex items-center">
                <input type="checkbox" checked={isHighlighted} onChange={e => setHighlighted(e.target.checked)} className="w-4 h-4 text-wine-100 border-gray-300 focus:ring-0 accent-wine-100" />
                <div className="ml-3">
                    <p className="font-semibold text-gray-800">Set Highlight</p>
                    <p className="text-xs text-gray-500">Legal Foundation</p>
                </div>
            </label>
        </div>
      </div>
    </div>
  );
};

export default PublicationEditor;